<script setup>
import { ref, computed, watch } from "vue";
import McusDataService from "@/services/McusDataService";
import { readAndCompressImage } from "browser-image-resizer";
import * as bootstrap from "bootstrap";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
 
const store = useStore();
 
const fileInput = ref(null);
 
const isCheckVinButtonActive = computed(() => store.state.inputVin.length === 17);
 
// Read VIN（Read VIN ボタン処理時）loading flag
const isLoadingReadVin = ref(false);
// Check VIN（Check VIN ボタン処理時）loading flag
const isLoadingCheckVin = ref(false);
 
watch(
  () => store.state.inputVin,
  (newValue) => {
    // 全角文字を半角に変換し、大文字に変換する
    store.commit(
      "setInputVin",
      newValue
        .replace(/[\uff01-\uff5e]/g, function (ch) {
          return String.fromCharCode(ch.charCodeAt(0) - 0xfee0);
        })
        .toUpperCase()
    );
  }
);
 
const router = useRouter();
 
// エラーモーダルのメッセージ
const errorMessage = ref("");

// エラーメッセージ定数
const ERROR_MESSAGES = {
  vinNotFound:      "The VIN is not available",
  vinReadClient:    "Failed to read VIN. Please retake a photo and try again.",
  vinReadServer:    "Failed to read VIN due to a server error. Please try again later.",
  vinReadNetwork:   "Unable to read VIN. Please check your network and try again.",
  vinCheckClient:   "Failed to check VIN. Please check the entered VIN and try again.",
  vinCheckServer:   "Failed to check VIN due to a server error. Please try again later.",
  vinCheckNetwork:  "Unable to check VIN. Please check your network and try again.",
};

// エラーモーダルを表示する関数
const showError = (message) => {
  errorMessage.value = message;
  const modal = new bootstrap.Modal(document.getElementById("errorModal"));
  modal.show();
};
 
// CheckVINボタンを押した後チェック結果を処理する関数
const handleVinCheckResult = (response) => {
  if (response.data.status == "Found") {
    console.log("VIN found.");
    router.push({ name: "ChecklistCapturePage" });
  } else if (response.data.status === "Not Found") {
    showError(ERROR_MESSAGES.vinNotFound);
  }
};
 
// CheckVINボタンを押した後エラーを処理する関数
const handleVinCheckError = (e) => {
  if (e.response) {
    if (e.response.data.error === "versionError") {
      window.alert(e.response.data.message);
      return;
    }
    if (e.response.status >= 500) {
      showError(ERROR_MESSAGES.vinCheckServer);
      return;
    }
    showError(ERROR_MESSAGES.vinCheckClient);
  } else {
    showError(ERROR_MESSAGES.vinCheckNetwork);
  }
};
 
// CheckVINボタンを押した後のVIN照合処理
const isCheckVinShipped = () => {
  event.preventDefault();
  isLoadingCheckVin.value = true;
  McusDataService.getVinCheckedResult({
    inputVINData: store.state.inputVin,
    log: store.state.logData,
  })
    .then(handleVinCheckResult)
    .catch(handleVinCheckError)
    .finally(() => {
      isLoadingCheckVin.value = false;
    });
};
 
// キーボードを閉じるための変数
const isPressedEnter = ref(null);
 
// ファイル入力をトリガーする関数
function startCaptureVinImage() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}
 
// 画像を圧縮する関数
async function compressImage(file) {
  if (file) {
    const config = {
      quality: 0.9,
      maxWidth: 800,
      maxHeight: 800,
      autoRotate: true,
      debug: true,
    };
    try {
      return await readAndCompressImage(file, config);
    } catch (error) {
      console.error("Error while compressing image", error);
    }
  }
  return file;
}
 
// 画像をキャプチャしたときに呼び出される関数
async function handleCaptureVinImage(event) {
  event.preventDefault();
  isLoadingReadVin.value = true;
  let file = event.target.files[0];
  file = await compressImage(file);
  const vinImageBase64Url = await convertToDataURL(file);
  const imageb64 = vinImageBase64Url.split(",")[1];
 
  McusDataService.getVinReadResult({ vinImage: imageb64, log: store.state.logData })
    .then((response) => {
      if (response.data.vin) {
        store.commit("setInputVin", response.data.vin);
      } else {
        showError(ERROR_MESSAGES.vinReadClient);
      }
    })
    .catch((e) => {
      if (e.response && e.response.status >= 500) {
        showError(ERROR_MESSAGES.vinReadServer);
      } else if (!e.response) {
        showError(ERROR_MESSAGES.vinReadNetwork);
      } else {
        showError(ERROR_MESSAGES.vinReadClient);
      }
    })
    .finally(() => {
      isLoadingReadVin.value = false;
    });
}
 
// 画像ファイルをbase64 URLに変換する関数
function convertToDataURL(image) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(image);
  });
}
 
// キーボードを閉じる関数
function closeKeyboard() {
  if (isPressedEnter.value) {
    isPressedEnter.value.blur();
  }
}
</script>
 
<template>
  <!-- loading spinner 表示（Read VIN, Check VIN ボタン押し下げ時） -->
  <div v-if="isLoadingCheckVin || isLoadingReadVin" class="loading">
    <div class="spinner-border text-light" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
  <!-- エラーモーダル（共通） -->
  <div
    class="modal fade"
    id="errorModal"
    tabindex="-1"
    aria-hidden="true"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header"></div>
        <div class="modal-body">
          <p class="text-danger fw-bold">{{ errorMessage }}</p>
        </div>
        <div class="modal-footer justify-content-center">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
  <div id="header" class="pt-3 pb-3 text-start">Step1: Check VIN</div>
  <form>
    <div class="form-group">
      <label class="fs-8">Distributor</label>
      <p class="distributor-name">{{ store.getters.companyName }}</p>
    </div>
    <div class="form-group">
      <label class="fs-8">Read VIN</label>
      <!-- Captureボタンを「VIN」ラベルのすぐ下に配置 -->
      <div class="text-center">
        <!-- 入力を受付するインプット要素 -->
        <input
          type="file"
          accept="image/*"
          capture="environment"
          @change="handleCaptureVinImage"
          style="display: none"
          ref="fileInput"
        />
        <button
          class="btn btn-primary d-flex align-items-center mx-3 button-style"
          style="border: 1px solid #007bff; background-color: white; color: #007bff"
          type="button"
          @click="startCaptureVinImage"
        >
          <i class="bi bi-camera camera-icon"></i>
        </button>
      </div>
      <input
        class="form-control mt-3 vin-input"
        type="text"
        v-model="store.state.inputVin"
        placeholder="Input VIN"
        maxlength="17"
        ref="isPressedEnter"
        @keydown.enter="closeKeyboard"
      />
      <p class="error-message" v-if="store.state.inputVin.length === 0 || isCheckVinButtonActive"><br /></p>
      <p v-else class="error-message">Input 17-digits VIN</p>
    </div>
  </form>
  <div>
    <p class="error-message caution">
      Japanese production models only
    </p>
    <p class="error-message caution">
      M6 or later models only
    </p>
    <button
      class="btn btn-primary btn-lg button-style"
      :disabled="!isCheckVinButtonActive"
      @click="isCheckVinShipped()"
    >
      Check VIN
    </button>
  </div>
</template>
 
<style scoped>
#header {
  font-size: 120%; /* 文字サイズを大きく設定 */
  margin-left: 10px;
  font-weight: bold;
  margin-bottom: 80px; /* 必要なマージンを増加させます */
}
 
form {
  display: flex;
  flex-direction: column;
  align-items: center;
}
 
.form-group {
  margin-bottom: 25px;
  width: 100%;
  max-width: 300px;
}
 
label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 1.2em; /* 文字サイズを大きく設定 */
}
 
.caution {
  margin-top: -25px;
}
 
.vin-warning {
  line-height: 1.1;   /* 行間を狭くする */
  margin-bottom: 2px; /* 下の余白を小さくする */
  margin-top: 2px;    /* 上の余白も小さくする */
}
 
.form-select {
  width: 100%;
}
 
.form-select,
.form-control {
  width: 100%;
  margin-bottom: 5px;
  font-size: 1.2em; /* 文字サイズを大きく設定 */
}
 
.vin-input {
  font-size: 1.2em; /* インプット欄の文字サイズを大きく設定 */
}
 
button:disabled {
  background-color: #cccccc; /* 無効時の背景色 */
  cursor: not-allowed; /* カーソルを変更 */
  color: #666666; /* 無効時のテキスト色 */
  border-color: #666666;
  font-weight: bold;
}
 
/* エラーメッセージのスタイル */
.error-message {
  color: red; /* 赤文字で表示する */
  font-size: 1.2em; /* 文字サイズを大きく設定 */
}
 
/* ローディング状態を表示するスタイルを追加 */
.loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
/* モーダルの薄い横線の削除 */
.modal-header,
.modal-footer {
  border-bottom: none; /* 下線を除去 */
  border-top: none; /* 上線を除去 */
}
 
.text-center {
  display: flex;
  justify-content: center;
}
/* ReadVinボタンとCheckVinボタンのサイズとボタン内の文字位置を調整 */
.button-style {
  width: 150px;
  height: 50px;
  justify-content: center;
  align-items: center;
  font-size: 1.2em; /* ボタンの文字サイズを大きく設定 */
  font-weight: bold;
}
/* カメラアイコンのサイズ調整 */
.camera-icon {
  font-size: 35px;
}
</style>
